import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { postRequest } from "../../Requests/postRequests";
import { login } from "../../slices/staffSlice";
import { show } from '../../slices/toastSlice';
import Logo from "../../assets/images/logo.png";

function Login() {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [success, setSuccess] = useState<boolean>(false);
    const [process, setProcess] = useState<boolean>(false);

    const handleRequest = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setProcess(true);

        try {
            const res = await postRequest(`/auth/staff/login`, { email, password });
            setProcess(false);

            if (res && res.accessToken) {
                setSuccess(true);
                dispatch(login(res));
                navigate("/");
            } else {
                dispatch(show({ type: "error", state: true, message: res?.err || "Login failed" }));
            }
        } catch (error) {
            setProcess(false);
            dispatch(show({ type: "error", state: true, message: "Network error or server unavailable" }));
        }
    }, [dispatch, navigate, email, password]);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">

                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img src={Logo} alt="CedisPay" className="h-10 w-auto object-contain" />
                </div>

                {/* Card */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8">

                    {/* Heading */}
                    <div className="mb-7">
                        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                            Sign in
                        </h1>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Enter your credentials to access the portal
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleRequest} className="space-y-5">
                        <div>
                            <label htmlFor="email" className="form-label">Email address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="form-input"
                                placeholder="you@cedispay.com"
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label htmlFor="password" className="form-label !mb-0">Password</label>
                                <Link
                                    to="/forgot-password"
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="form-input"
                                placeholder="••••••••"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={process}
                            className="w-full h-11 flex items-center justify-center rounded-xl bg-light-blue hover:bg-dark-blue text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                        >
                            {process ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            ) : success ? (
                                <CheckCircleIcon className="w-5 h-5" />
                            ) : (
                                "Sign in"
                            )}
                        </button>
                    </form>
                </div>

                {/* Footer */}
                <p className="mt-5 text-center text-sm text-gray-500 dark:text-gray-400">
                    New here?{" "}
                    <Link to="/register" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
                        Create an account
                    </Link>
                </p>

            </div>
        </div>
    );
}

export default Login;
